import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Follower {
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
}

export function useFollowersList() {
  const { user } = useAuth();
  const [followers, setFollowers] = useState<Follower[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFollowers = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Get all friend relationships where current user is the friend_id (people who follow me)
      const { data: friendsData, error: friendsError } = await supabase
        .from('friends')
        .select('user_id')
        .eq('friend_id', user.id)
        .eq('status', 'accepted');

      if (friendsError) throw friendsError;

      if (!friendsData || friendsData.length === 0) {
        setFollowers([]);
        return;
      }

      // Then get the profiles for those users
      const userIds = friendsData.map(f => f.user_id);
      
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      const followersData: Follower[] = profilesData?.map((profile: any) => ({
        user_id: profile.user_id,
        full_name: profile.full_name || null,
        avatar_url: profile.avatar_url || null
      })) || [];

      setFollowers(followersData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch followers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchFollowers();

      // Set up real-time subscription for followers changes
      const channel = supabase
        .channel('followers-list-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'friends'
          },
          (payload: any) => {
            // Only refresh if the change involves the current user as friend_id (someone following/unfollowing me)
            if (payload.new && payload.new.friend_id === user.id) {
              fetchFollowers();
            } else if (payload.old && payload.old.friend_id === user.id) {
              fetchFollowers();
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  return {
    followers,
    loading,
    error,
    refetch: fetchFollowers
  };
}